import {withQuery} from 'ufo';
import type {RouteLocationRaw} from 'vue-router';
import {navigateTo} from '#imports';
import type {SafelyNavigateToOptions} from '../../types';
import type {NavigateToOptions} from '#app/composables/router';

const backslashRegex = /(\\)+/;
const placeholderOrigin = 'https://current-origin.invalid';

/* Checks if the URL is from an external domain
 * Ex: Starts with `https://` ,`http://`, `mailto:` etc..
 */
export function startsWithExternalScheme(url: string): boolean {
    const schemeRe = /^[a-z0-9.+-]+:(\/\/)?.*/i;
    return schemeRe.test(url);
}

/**
 * Converts a Vue route Location to a string URL. Parsing params and query params to a query string.
 */
export function toUrlString(to: RouteLocationRaw): string {
    // noinspection SuspiciousTypeOfGuard
    if (typeof to === 'string') {
        return to;
    }
    const path = 'path' in to ? (to.path ?? '/') : '/';
    const queryParams = to.query ?? {};
    const hash = to.hash ?? '';
    return withQuery(path, queryParams) + hash;
}

/**
 * Checks if the URL is an evil redirection by multiple factors
 * If the url is meant to be an external host, it will not check for external scheme.
 * All urls will be checked for control characters, backslashes, dots, at signs and @ signs.
 */
export function isEvilRedirection(url: string, isExternalHost = false): boolean {
    // Please, before changing anything here, read carefully that:
    // https://github.com/swisskyrepo/PayloadsAllTheThings/tree/master/Open%20Redirect
    // This is a list of common payload to bypass filters/sanitization in redirection URL

    const trimmedUrl = url.trim();
    const isUrlExternalHostScheme = isExternalHost ? false : startsWithExternalScheme(trimmedUrl);

    return (
        isUrlExternalHostScheme ||
        trimmedUrl.startsWith('//') ||
        backslashRegex.test(url) ||
        trimmedUrl.startsWith('.') ||
        url.includes('@') ||
        containsControlCharacters(url)
    );
}

/**
 * Sanitizes the URL by by removing host, scheme and special characters.
 */
export function sanitizedAndWithoutHostAndScheme(urlString: string): string {
    if (containsControlCharacters(urlString)) {
        return '/';
    }

    if (backslashRegex.test(urlString)) {
        return '/';
    }

    const trimmedUrlString = urlString.trim();

    if (startsWithExternalScheme(trimmedUrlString)) {
        const url = new URL(trimmedUrlString.replace(/^\//, ''));
        const urlFromPath = url.pathname + url.search + url.hash;
        return urlFromPath.startsWith('/') ? urlFromPath : `/${urlFromPath}`;
    }

    const urlFromPath = trimmedUrlString
        /* Handles leading dots (Ex: ".foo" -> "foo") */
        .replace(/^\.+/, '')
        /* Handle trailing multiple slashes (Ex: "///path//to///page" -> "/path/to/page") */
        .replaceAll(/\/+/g, '/')
        /* Handle multiple dots followed by a slash (Ex: "../../foo/" -> "foo/") */
        .replaceAll(/\.+\//g, '');
    return urlFromPath.startsWith('/') ? urlFromPath : `/${urlFromPath}`;
}

// Control characters have nothing to do inside a URL.
// https://www.w3schools.com/tags//ref_urlencode.asp
function containsControlCharacters(urlString: string): boolean {
    // eslint-disable-next-line no-control-regex
    return /%[0-1][0-9A-F]/.test(urlString) || /[\x00-\x1F]/.test(urlString);
}

export function isRelativeToCurrentOrigin(to: RouteLocationRaw): boolean {
    return new URL(toUrlString(to), placeholderOrigin).origin === placeholderOrigin;
}

/**
 * A drop-in replacement for Nuxt's navigateTo with slightly different options, to make it clear:
 * - when we don't want to leave the current Nuxt application (no option)
 * - when we only want to navigate to a different Nuxt application of the platform - meaning on
 * the same host ({externalToThisApp: true})
 * - or when we accept navigating to another host (`{externalToThisHost: true}`)
 * This helps to prevent any <a href="https://github.com/swisskyrepo/PayloadsAllTheThings/tree/master/Open%20Redirect">
 * Open URL Redirection</a>.
 *
 * Note: the issue with Nuxt's navigateTo is that in case we don't call it with `{external: true}`,
 * it will only accept paths handled by the current application's router, or will act as if these
 * paths should be handled by it.
 * For example, passing it /some-path-handled-by-another-app will result in it redirecting the
 * user to /app-base-url/some-path-handled-by-another-app (while emitting a warning in the console
 * when in dev mode). This makes sense given how a SPA works, but we need a way to leave the
 * application while still not allowing leaving the current host.
 *
 * @param to - The URL to navigate to. ('/' will be used when not provided)
 * @param options - The navigation options.
 */
export function safelyNavigateTo(to: RouteLocationRaw | undefined | null, options?: SafelyNavigateToOptions): ReturnType<typeof navigateTo> {
    try {
        if (to) {
            const urlString = toUrlString(to);
            const isEvil = isEvilRedirection(urlString, options?.externalToThisHost);

            if (isEvil && !options?.externalToThisHost) {
                // Instead of throwing an error, we sanitize the URL. This preserves a working
                // navigation, should the URL contain one of our host names.
                // This is the same strategy we use back-end side, in com.hopwork.httprequestutils.RedirectSecurityUtils.computeRedirectUrl
                // (see https://gitlab.com/maltcommunity/malt/apps/malt/-/blob/6f226d9d3927663f65fd7ec36da3ec20d7869118/lib-platform/httprequestutils/src/main/java/com/hopwork/httprequestutils/RedirectSecurityUtils.java#L12-15)

                to = sanitizedAndWithoutHostAndScheme(urlString);
            } else if (isEvil) {
                return navigateTo('/');
            }

            if (!options?.externalToThisHost && !isRelativeToCurrentOrigin(to)) {
                to = '/';
            }
        }

        const navigateToOptions: NavigateToOptions | undefined =
            options &&
            Object.assign({}, options, {
                external: options?.externalToThisApp || options?.externalToThisHost,
            });
        return navigateTo(to, navigateToOptions);
    } catch {
        return navigateTo('/');
    }
}
